import {
  ClickAwayListener,
  Grid2,
  IconButton,
  TableContainer,
} from "@mui/material";
import {
  DataGridPro,
  DataGridProProps,
  GridColDef,
  GridColumnResizeParams,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { useColumnsState } from "hooks/useColumnsState";
import { ReactNode, useEffect, useState } from "react";
import {
  generateColumnVisibilityState,
  updateFilter,
} from "store/slices/tableSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { getAppliedFilterCount } from "utils/helpers";
import { TableFiltering } from "utils/TableFilters";
import CommonPagination from "./CommanPagination";
import { CustomLoader, EmptyData, Pagination } from "./DatagridUtils";
import ExportCsv from "./ExportCsv";
import GKCheckbox from "./GKCheckbox";
import GKCommonTableFilterModal from "./GKCommonTableFilterModal";
import GKTextField from "./GKTextField";
import {
  AddColumnIcon,
  Down1Icon,
  FilterIcon,
  SearchIcon,
  SettingsIcon,
} from "./Icons";
import { Show } from "./Show";
import SortByPopover from "./SortByPopover";
import TableColumnModal from "./TableColumnModal";

interface Props {
  headerComponent?: any;
  showSearch?: boolean;
  columnsButton?: boolean;
  ref?: any;
  maxHeight?: any;
  showHeaderGrid?: any;
  zeroHolding?: boolean;
  minHeight?: any;
  height?: string;
  tableName?: string;
  name: string;
  exportFile?: {
    path?: string;
    exportName?: string;
    paramsValue?: {
      key?: string;
      value?: string;
    };
  };
  emptyButtons?: any;
  totalCount?: number;
  disabledColumns?: string[];
  extraComponent?: any;
  sortBy?: boolean;
  emptyComponent?: ReactNode;
}

const GKDataGrid = (props: DataGridProProps & Props) => {
  const dispatch = useAppDispatch();

  const columnsPersist = useAppSelector(
    (state) => state?.table?.columnsPersist,
  );

  const {
    ref,
    rows,
    name,
    height,
    columns,
    loading,
    minHeight,
    tableName,
    maxHeight,
    exportFile,
    onRowClick,
    totalCount,
    zeroHolding,
    pinnedColumns,
    headerComponent,
    showSearch = true,
    columnsButton = true,
    showHeaderGrid = true,
    disabledColumns,
    extraComponent,
    emptyButtons,
    sortBy,
    emptyComponent,
    ...restProps
  } = props;

  const commonFilter = useAppSelector(
    (state) => state?.table?.commonFilter[name],
  );

  const [tableColumnModalOpen, setTableColumnModalOpen] = useState(false);
  const [filterModal, setFilterModal] = useState(false);
  const [searchText, setSearchText] = useState(commonFilter?.search);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const {
    apiRef,
    columns: computedColumns,
    onColumnOrderChange,
    onColumnWidthChange,
    onColumnCellDoubleClickWidthChange,
  } = useColumnsState({
    columns,
    name: name,
  });

  useEffect(() => {
    const timerId = setTimeout(() => {
      dispatch(updateFilter({ name, filter: { search: searchText?.trim() } }));
    }, 400);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchText]);

  function CheckboxWrapper(props: any) {
    return <GKCheckbox {...props} />;
  }

  const exportCsv = () => {
    return (
      <ExportCsv
        path={exportFile?.path}
        name={exportFile?.exportName}
        paramsValue={exportFile?.paramsValue}
        filter={commonFilter}
      />
    );
  };

  const CustomPagination = () => {
    return <CommonPagination name={name} totalCount={totalCount} />;
  };

  const handleSortModelChange = (sortModel: GridSortModel) => {
    const sortBy = commonFilter?.sortBy;

    const mappedSortModel = sortModel.map((sort) => {
      const column: GridColDef & { sortField?: string[] } = columns.find(
        (col) => col.field === sort.field,
      );
      const dynamicField =
        (column?.sortField &&
        Array.isArray(column.sortField) &&
        sortBy === "percentage"
          ? column.sortField[1]
          : column?.sortField?.[0]) || sort.field;

      return {
        field: dynamicField,
        sort: sort.sort,
      };
    });

    dispatch(updateFilter({ name, filter: { sortModel: mappedSortModel } }));
  };

  const appliedFilterCount = getAppliedFilterCount(commonFilter?.customFilter);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  return (
    <Grid2
      container
      sx={{
        "& .MuiTableContainer-root": {
          borderTopRightRadius: 0,
          borderTopLeftRadius: 0,
        },
        "& .MuiDataGrid-virtualScroller": {
          maxHeight: maxHeight || "calc(100vh - 256px)",
          minHeight: minHeight || "10px",
        },
        "&.MuiDataGrid-root .MuiTablePagination-root": {
          padding: "0 8px",
        },
        "& .MuiDataGrid-row": {
          "&:hover": {
            cursor: onRowClick ? "pointer" : "",
          },
        },
        "& .MuiToolbar-gutters": {
          ml: "0 !important",
          borderTop: "1px solid var(--grey-100)",
        },
      }}
    >
      <Show.When isTrue={showHeaderGrid}>
        <Grid2 size={12}>
          <Grid2
            container
            justifyContent={"space-between"}
            p={1}
            alignItems={"center"}
            ref={ref}
            border={`solid 1px var(--neutral-100)`}
            borderBottom={`solid 0px var(--neutral-100)`}
            sx={{
              borderTopRightRadius: 8,
              borderTopLeftRadius: 8,
            }}
          >
            <Grid2
              size={"grow"}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Show.When isTrue={showSearch}>
                <GKTextField
                  fullWidth={false}
                  style={{ width: 180 }}
                  placeholder="Search"
                  sx={{
                    "& .MuiInputBase-root ": {
                      height: "35px",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "1px solid #D5DEE5",
                    },
                    background: "#F8F8F8",
                    color: "var(--black)",
                  }}
                  value={searchText}
                  onChange={(event: any) => {
                    setSearchText(event.target.value);
                  }}
                  type="search"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <SearchIcon
                          width={22}
                          height={22}
                          color={"var(--black)"}
                        />
                      ),
                    },
                  }}
                />
              </Show.When>
              <Show.When isTrue={Boolean(tableName)}>
                <IconButton onClick={() => setFilterModal(true)}>
                  <FilterIcon width={16} height={16} />
                </IconButton>
              </Show.When>
              <Show.When isTrue={!!exportFile?.path}>{exportCsv()}</Show.When>
              <Show.When isTrue={columnsButton}>
                <IconButton
                  onClick={() => {
                    setTableColumnModalOpen(true);
                  }}
                >
                  <AddColumnIcon />
                </IconButton>
              </Show.When>
              <Show.When isTrue={!!sortBy}>
                <IconButton
                  sx={{ borderRadius: 4, bgcolor: !!anchorEl && " #EEF5FA" }}
                  onClick={handleClick}
                >
                  <SettingsIcon />
                </IconButton>
              </Show.When>
            </Grid2>
            <Show.When isTrue={headerComponent}>
              <Grid2 size={"grow"} display={"flex"} justifyContent={"flex-end"}>
                {headerComponent}
              </Grid2>
            </Show.When>
          </Grid2>
          <Show.When isTrue={!!extraComponent}>
            <Grid2
              size={12}
              display={"flex"}
              alignItems={"center"}
              p={1}
              borderLeft={`solid 1px var(--neutral-100)`}
              borderRight={`solid 1px var(--neutral-100)`}
            >
              {extraComponent}
            </Grid2>
          </Show.When>
          <Show.When isTrue={appliedFilterCount !== 0}>
            <Grid2
              size={12}
              display={"flex"}
              alignItems={"center"}
              px={1}
              pb={1}
              borderLeft={`solid 1px var(--neutral-100)`}
              borderRight={`solid 1px var(--neutral-100)`}
            >
              <TableFiltering name={name} />
            </Grid2>
          </Show.When>
        </Grid2>
      </Show.When>
      <Grid2 size={12}>
        <TableContainer>
          <DataGridPro
            {...restProps}
            onRowClick={onRowClick}
            columnVisibilityModel={
              name &&
              (columnsPersist?.[name] ||
                (columns.length > 0
                  ? generateColumnVisibilityState(
                      columns.map((col) => col.field),
                    )
                  : undefined))
            }
            apiRef={apiRef}
            getDetailPanelHeight={() => "auto"}
            sortingMode="server"
            filterMode="server"
            keepNonExistentRowsSelected
            disableRowSelectionOnClick
            sortModel={(commonFilter?.sortModel || []).map((sort) => {
              const matchingColumn = columns.find(
                (col: GridColDef & { sortField?: string[] }) => {
                  if (!col.sortField) return false;
                  return col.sortField.includes(sort.field);
                },
              );
              return {
                field: matchingColumn?.field || sort.field,
                sort: sort.sort,
              };
            })}
            loading={loading}
            rows={loading ? [] : rows || []}
            columns={computedColumns}
            onColumnOrderChange={onColumnOrderChange}
            onColumnResize={(params: GridColumnResizeParams) => {
              onColumnWidthChange(params);
            }}
            onColumnHeaderDoubleClick={(params: any) => {
              if (params.colDef.minWidth !== params.colDef.computedWidth) {
                onColumnCellDoubleClickWidthChange({
                  colDef: params,
                  width: params.colDef.minWidth,
                });
              }
            }}
            onSortModelChange={handleSortModelChange}
            slots={{
              noRowsOverlay: () =>
                emptyComponent || <EmptyData emptyButtons={emptyButtons} />,
              loadingOverlay: CustomLoader,
              detailPanelExpandIcon: () => <Down1Icon width={20} height={20} />,
              detailPanelCollapseIcon: () => (
                <Down1Icon
                  style={{
                    transform: " rotate(180deg)",
                  }}
                  width={20}
                  height={20}
                />
              ),
              footer: totalCount ? CustomPagination : Pagination,
              baseCheckbox: CheckboxWrapper,
            }}
            pinnedColumns={{
              ...pinnedColumns,
              right: ["action"],
            }}
            sx={{
              minHeight: height,
              maxHeight: height,
            }}
          />
        </TableContainer>
      </Grid2>
      {filterModal && tableName && (
        <GKCommonTableFilterModal
          open={filterModal}
          setOpen={setFilterModal}
          tableName={tableName}
          name={name}
          zeroHolding={zeroHolding}
        />
      )}

      <Show.When isTrue={tableColumnModalOpen}>
        <TableColumnModal
          open={tableColumnModalOpen}
          setOpen={setTableColumnModalOpen}
          columns={columns}
          columnVisibilityModel={columnsPersist || {}}
          name={name}
          disabledColumns={disabledColumns}
        />
      </Show.When>

      {!!anchorEl && (
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <SortByPopover open={anchorEl} setOpen={setAnchorEl} name={name} />
        </ClickAwayListener>
      )}
    </Grid2>
  );
};

export default GKDataGrid;
